import { useCallback, useEffect, useRef, useState } from 'react'

export interface VideoPreviewState {
  videoRef: React.RefObject<HTMLVideoElement | null>
  streamRef: React.RefObject<MediaStream | null>
  isEnabled: boolean
  setEnabled: (enabled: boolean) => void
  deviceId: string
  setDeviceId: (deviceId: string) => void
  stopPreview: () => void
}

export function useVideoPreview(initialDeviceId = ''): VideoPreviewState {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [isEnabled, setEnabled] = useState(true)
  const [deviceId, setDeviceId] = useState(initialDeviceId)

  const stopPreview = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop()
      })
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  // Start/stop preview based on enabled state and device
  useEffect(() => {
    const startPreview = async () => {
      // Stop existing stream first
      stopPreview()

      if (!isEnabled) return

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: deviceId ? { deviceId } : true,
          audio: false,
        })
        streamRef.current = stream

        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err) {
        console.error('Error starting video preview:', err)
      }
    }

    startPreview()
  }, [isEnabled, deviceId, stopPreview])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPreview()
    }
  }, [stopPreview])

  return {
    videoRef,
    streamRef,
    isEnabled,
    setEnabled,
    deviceId,
    setDeviceId,
    stopPreview,
  }
}
